<!-- Copyright ©2026 Hitachi High-Tech Corporation All Rights Reserved. -->
<ng-container>
  <!-- dialog header -->
  <h2 mat-dialog-title class="dialog-title">
    Filter
    <span class="spacer"></span>
    <button
      [class]="isEditButtonDisable ? 'btn-text-disabled' : 'btn-text'"
      mat-stroked-button
      [disabled]="isEditButtonDisable"
      (click)="openSection('saves')">
        <app-icons [icon]="'save'" />
        Save / Load
    </button>
  </h2>
  <mat-dialog-content class="dialog-content">
    <mat-drawer-container class="drawer-container" >
      <!-- Left mini-nav -->
      <mat-drawer mode="side" opened class="mini-nav" position="start" [disableClose]="true">
        <mat-nav-list dense>
          <ng-container *ngFor="let s of sections">
            <mat-divider *ngIf="s.key == 'saves'"></mat-divider>
            <a [disabled]="isDisabledFilterType(s.key)" mat-list-item (click)="openSection(s.key)">
              <span class="label">{{ s.label }}</span>
              <span class="count" *ngIf="getSectionCount(s.key) > 0">{{ getSectionCount(s.key) }}</span>
            </a>
          </ng-container>
        </mat-nav-list>
      </mat-drawer>
      <!-- Right drawer: Editors -->
      <mat-drawer class="editor-drawer" mode="over" position="end" [opened]="drawerOpened" (closedStart)="closeDrawer()" (closed)="onDrawerClosed()">
        <!-- 1/4:header -->
        <div class="editor-header sticky">
          <div class="title">
            <mat-icon>tune</mat-icon>
            <span>{{ currentSectionLabel }}</span>
          </div>
          <button class="close-btn" mat-icon-button (click)="closeDrawer()" aria-label="Close editor">
            <app-icons icon="close" />
          </button>
        </div>
        <!-- 2/4:action bar -->
        <ng-container *ngIf="activeSection !== 'saves'">
          <div class="editor-actionbar sticky">
            <button
              class="btn-text"
              mat-stroked-button
              (click)="onClearFilter(activeSection)"
            >
              Clear Filter
            </button>
            <app-buttons
              name="OK"
              buttonType="mat-flat-button"
              [buttonStyle]="isInvalid(activeSection) ? 'btn-filled-small-disabled' : 'btn-filled-small'"
              [isDisabled]="isInvalid(activeSection)"
              (buttonClickEvent)="persistEdit(activeSection)"
            ></app-buttons>
          </div>
        </ng-container>
        <!-- 3/4:configurable items -->
        <ng-container *ngIf="isUseAdd(activeSection)">
          <div class="editor-addbar sticky">
            <!-- Physical Position / Hide / SV Length / Match to Annotation -->
            <div class="pos-toolbar">
              <button class="add-btn" mat-mini-fab color="primary" (click)="addDefaultParameter(activeSection)">
                <app-icons icon="add" />
              </button>
              <span class="pos-count">
                {{ getEditingParameterCount(activeSection) }} filters(s)
              </span>
            </div>
          </div>
        </ng-container>
        <!-- 4/4:settings -->
        <div class="editor-body" *ngIf="activeSection as sec">
          <!-- Physical Position -->
          <ng-container *ngIf="sec === 'physicalPosition'">
            <div class="param-list">
              <div class="physical-position-row" *ngFor="let g of posItems.controls; let i = index" [formGroup]="g">
                <mat-label>OR</mat-label>
                <mat-form-field appearance="outline" floatLabel="always" class="chromosome">
                  <mat-label>Chromosome</mat-label>
                  <mat-select formControlName="chr">
                    <mat-option *ngFor="let c of chrs" [value]="c.value">{{ c.label }}</mat-option>
                  </mat-select>
                </mat-form-field>
                <mat-form-field appearance="outline" floatLabel="always">
                  <mat-label>Position Start</mat-label>
                  <input
                    matInput
                    inputmode="numeric"
                    formControlName="posStart"
                    [placeholder]="POSITION_MIN + ' - ' + POSITION_MAX"
                    [errorStateMatcher]="bothEmptyMatcher"
                    rangeValidator
                    [min]="POSITION_MIN"
                    [max]="POSITION_MAX"
                    autocomplete="off"
                  >
                  <mat-error *ngIf="posItems.at(i).get('posStart')?.errors?.['whitespace']">
                    Spaces are not allowed
                  </mat-error>
                  <mat-error *ngIf="!posItems.at(i).get('posStart')?.errors?.['whitespace'] && posItems.at(i).get('posStart')?.errors">
                    Input numbers(range. {{POSITION_MIN}} - {{POSITION_MAX}})
                  </mat-error>
                  <mat-error *ngIf="!posItems.at(i).get('posStart')?.errors">
                    Either Position Start or Position End is required
                  </mat-error>
                </mat-form-field>
                <mat-form-field appearance="outline" floatLabel="always">
                  <mat-label>Position End</mat-label>
                  <input
                    matInput
                    inputmode="numeric"
                    formControlName="posEnd"
                    [placeholder]="POSITION_MIN + ' - ' + POSITION_MAX"
                    [errorStateMatcher]="bothEmptyMatcher"
                    rangeValidator
                    [min]="POSITION_MIN"
                    [max]="POSITION_MAX"
                    autocomplete="off"
                  >
                  <mat-error *ngIf="posItems.at(i).get('posEnd')?.errors?.['whitespace']">
                    Spaces are not allowed
                  </mat-error>
                  <mat-error *ngIf="!posItems.at(i).get('posEnd')?.errors?.['whitespace'] && posItems.at(i).get('posEnd')?.errors">
                    Input numbers(range. {{POSITION_MIN}} - {{POSITION_MAX}})
                  </mat-error>
                  <mat-error *ngIf="!posItems.at(i).get('posEnd')?.errors">
                    Either Position Start or Position End is required
                  </mat-error>
                </mat-form-field>
                <mat-form-field appearance="outline" class="unit">
                  <mat-label>Unit</mat-label>
                  <mat-select formControlName="posBp">
                    <mat-option *ngFor="let u of unitsKMG" [value]="u.value">{{ u.label }}</mat-option>
                  </mat-select>
                </mat-form-field>
                <button class="row-component" mat-icon-button (click)="removePosition(sec, i)" aria-label="Remove condition">
                  <app-icons icon="delete" />
                </button>
              </div>
            </div>
          </ng-container>
          <!-- Physical Position(Hide) -->
          <ng-container *ngIf="sec === 'physicalPositionHide'">
            <div class="param-list">
              <div class="physical-position-row" *ngFor="let g of posHideItems.controls; let i = index" [formGroup]="g">
                <mat-label>AND</mat-label>
                <mat-form-field appearance="outline" floatLabel="always">
                  <mat-label>Chromosome</mat-label>
                  <mat-select formControlName="chr">
                    <mat-option *ngFor="let c of chrs" [value]="c.value">{{ c.label }}</mat-option>
                  </mat-select>
                </mat-form-field>
                <mat-form-field appearance="outline" floatLabel="always">
                  <mat-label>Position Start</mat-label>
                  <input
                    matInput
                    inputmode="numeric"
                    formControlName="posStart"
                    [placeholder]="POSITION_MIN + ' - ' + POSITION_MAX"
                    [errorStateMatcher]="bothEmptyMatcher"
                    rangeValidator
                    [min]="POSITION_MIN"
                    [max]="POSITION_MAX"
                    autocomplete="off"
                  >
                  <mat-error *ngIf="posHideItems.at(i).get('posStart')?.errors?.['whitespace']">
                    Spaces are not allowed
                  </mat-error>
                  <mat-error *ngIf="!posHideItems.at(i).get('posStart')?.errors?.['whitespace'] && posHideItems.at(i).get('posStart')?.errors">
                    Input numbers(range. {{POSITION_MIN}} - {{POSITION_MAX}})
                  </mat-error>
                  <mat-error *ngIf="!posHideItems.at(i).get('posStart')?.errors">
                    Either Position Start or Position End is required
                  </mat-error>
                </mat-form-field>
                <mat-form-field appearance="outline" floatLabel="always">
                  <mat-label>Position End</mat-label>
                  <input
                    matInput
                    inputmode="numeric"
                    formControlName="posEnd"
                    [placeholder]="POSITION_MIN + ' - ' + POSITION_MAX"
                    [errorStateMatcher]="bothEmptyMatcher"
                    rangeValidator
                    [min]="POSITION_MIN"
                    [max]="POSITION_MAX"
                    autocomplete="off"
                  >
                  <mat-error *ngIf="posHideItems.at(i).get('posEnd')?.errors?.['whitespace']">
                    Spaces are not allowed
                  </mat-error>
                  <mat-error *ngIf="!posHideItems.at(i).get('posEnd')?.errors?.['whitespace'] && posHideItems.at(i).get('posEnd')?.errors">
                    Input numbers(range. {{POSITION_MIN}} - {{POSITION_MAX}})
                  </mat-error>
                  <mat-error *ngIf="!posHideItems.at(i).get('posEnd')?.errors">
                    Either Position Start or Position End is required
                  </mat-error>
                </mat-form-field>
                <mat-form-field appearance="outline" class="unit">
                  <mat-label>Unit</mat-label>
                  <mat-select formControlName="posBp">
                    <mat-option *ngFor="let u of unitsKMG" [value]="u.value">{{ u.label }}</mat-option>
                  </mat-select>
                </mat-form-field>
                <button class="row-component" mat-icon-button (click)="removePosition(sec, i)" aria-label="Remove condition">
                  <app-icons [icon]="'delete'" />
                </button>
              </div>
            </div>
          </ng-container>
          <!-- SV Length -->
          <ng-container *ngIf="sec === 'svLength'">
            <div class="param-list">
              <div class="sv-length-row" *ngFor="let g of svLengthItems.controls; let i = index" [formGroup]="g">
                <mat-label>OR</mat-label>
                <mat-form-field appearance="outline" floatLabel="always">
                  <mat-label>min</mat-label>
                  <input
                    matInput
                    inputmode="numeric"
                    formControlName="lenMin"
                    [placeholder]="POSITION_MIN + ' - ' + POSITION_MAX"
                    [errorStateMatcher]="bothEmptyMatcher"
                    rangeValidator
                    [min]="POSITION_MIN"
                    [max]="POSITION_MAX"
                    autocomplete="off"
                  >
                  <mat-error *ngIf="svLengthItems.at(i).get('lenMin')?.errors?.['whitespace']">
                    Spaces are not allowed
                  </mat-error>
                  <mat-error *ngIf="!svLengthItems.at(i).get('lenMin')?.errors?.['whitespace'] && svLengthItems.at(i).get('lenMin')?.errors">
                    Input numbers(range. {{POSITION_MIN}} - {{POSITION_MAX}})
                  </mat-error>
                  <mat-error *ngIf="!svLengthItems.at(i).get('lenMin')?.errors">
                    Either min or max is required
                  </mat-error>
                </mat-form-field>
                <mat-form-field appearance="outline" class="unit">
                  <mat-label>Unit</mat-label>
                  <mat-select formControlName="lenMinBp">
                    <mat-option *ngFor="let u of unitsKM" [value]="u.value">{{ u.label }}</mat-option>
                  </mat-select>
                </mat-form-field>
                <mat-form-field appearance="outline" floatLabel="always">
                  <mat-label>max</mat-label>
                  <input
                    matInput
                    inputmode="numeric"
                    formControlName="lenMax"
                    [placeholder]="POSITION_MIN + ' - ' + POSITION_MAX"
                    [errorStateMatcher]="bothEmptyMatcher"
                    rangeValidator
                    [min]="POSITION_MIN"
                    [max]="POSITION_MAX"
                    autocomplete="off"
                  >
                  <mat-error *ngIf="svLengthItems.at(i).get('lenMax')?.errors?.['whitespace']">
                    Spaces are not allowed
                  </mat-error>
                  <mat-error *ngIf="!svLengthItems.at(i).get('lenMax')?.errors?.['whitespace'] && svLengthItems.at(i).get('lenMax')?.errors">
                    Input numbers(range. {{POSITION_MIN}} - {{POSITION_MAX}})
                  </mat-error>
                  <mat-error *ngIf="!svLengthItems.at(i).get('lenMax')?.errors">
                    Either min or max is required
                  </mat-error>
                </mat-form-field>
                <mat-form-field appearance="outline" class="unit">
                  <mat-label>Unit</mat-label>
                  <mat-select formControlName="lenMaxBp">
                    <mat-option *ngFor="let u of unitsKM" [value]="u.value">{{ u.label }}</mat-option>
                  </mat-select>
                </mat-form-field>
                <button class="row-component" mat-icon-button (click)="removePosition(sec, i)" aria-label="Remove condition">
                  <app-icons [icon]="'delete'" />
                </button>
              </div>
            </div>
          </ng-container>
          <!-- Match to Annotation -->
          <ng-container *ngIf="sec === 'matchAnnotation'">
            <div class="param-list">
              <div class="match-annotation-row" *ngFor="let g of matchAnnotationItems.controls; let i = index" [formGroup]="g">
                <mat-form-field appearance="outline" class="annotation">
                  <mat-label>Annotation</mat-label>
                  <mat-select formControlName="annotation" [matTooltip]="g?.get('annotation')?.value ?? ''" matTooltipShowDelay="300">
                    <mat-option *ngFor="let u of annotations" [value]="u" [matTooltip]="u" matTooltipShowDelay="300">{{ u }}</mat-option>
                  </mat-select>
                </mat-form-field>
                <mat-form-field appearance="outline" floatLabel="always">
                  <mat-label>Threshold</mat-label>
                  <input
                    matInput
                    inputmode="numeric"
                    formControlName="threshold"
                    [placeholder]="POSITION_MIN + ' - ' + POSITION_MAX"
                    rangeValidator
                    [min]="POSITION_MIN"
                    [max]="POSITION_MAX"
                    autocomplete="off"
                  >
                  <mat-error *ngIf="matchAnnotationItems.at(i).get('threshold')?.errors?.['required']">
                    Threshold is required
                  </mat-error>
                  <mat-error *ngIf="matchAnnotationItems.at(i).get('threshold')?.errors?.['whitespace']">
                    Spaces are not allowed
                  </mat-error>
                  <mat-error *ngIf="
                    !matchAnnotationItems.at(i).get('threshold')?.errors?.['whitespace'] &&
                    !matchAnnotationItems.at(i).get('threshold')?.errors?.['required'] &&
                    matchAnnotationItems.at(i).get('threshold')?.errors"
                  >
                    Input numbers(range. {{POSITION_MIN}} - {{POSITION_MAX}})
                  </mat-error>
                </mat-form-field>
                <mat-form-field appearance="outline" class="unit">
                  <mat-label>Unit</mat-label>
                  <mat-select formControlName="thresholdunit">
                    <mat-option *ngFor="let u of unitsPercent" [value]="u.value">{{ u.label }}</mat-option>
                  </mat-select>
                </mat-form-field>
                <mat-checkbox class="row-component" formControlName="excludeMatches">Exclude Matches</mat-checkbox>
                <button class="row-component" mat-icon-button (click)="removePosition(sec, i)" aria-label="Remove condition">
                  <app-icons [icon]="'delete'" />
                </button>
              </div>
            </div>
          </ng-container>
          <!-- SV Type -->
          <ng-container *ngIf="sec === 'svType'">
            <div class="checkbox-group">
              <mat-checkbox *ngFor="let key of svTypeKeys" [(ngModel)]="selectSvTypeByKey[key]">{{ SvFilterSvType[key] }}</mat-checkbox>
            </div>
          </ng-container>
          <!-- Zygosity -->
          <ng-container *ngIf="sec === 'zygosity'">
            <div class="checkbox-group">
              <mat-checkbox *ngFor="let key of zygosityKeys" [(ngModel)]="selectZygosityByKey[key]">{{ SvFilterZygosity[key] }}</mat-checkbox>
            </div>
          </ng-container>
          <!-- Flagged SV -->
          <ng-container *ngIf="sec === 'flaggedSV'">
            <div class="checkbox-group">
              <mat-checkbox *ngFor="let key of flaggedSvKeys" [(ngModel)]="selectFlaggedSvByKey[key]">{{ SvFilterFlaggedSv[key] }}</mat-checkbox>
            </div>
          </ng-container>
          <!-- Gene Name -->
          <ng-container *ngIf="sec === 'geneName'">
            <form [formGroup]="geneNameForm">
              <mat-form-field appearance="outline" floatLabel="always" class="gene-name-textarea">
                <mat-label>Gene Name</mat-label>
                <textarea
                  matInput
                  inputmode="numeric"
                  formControlName="geneName"
                  matTextareaAutosize
                  rows="5"
                  placeholder=""
                ></textarea>
                <mat-error *ngIf="geneNameForm.get('geneName')?.hasError('maxlength')">
                  Input must be {{GENE_NAME_MAX_LENGTH}} characters or fewer.
                </mat-error>
              </mat-form-field>
            </form>
          </ng-container>
          <!-- SVs with Ref Genes detected -->
          <ng-container *ngIf="sec === 'svsWithRefGenesDetected'">
            <div class="checkbox-group">
              <mat-checkbox *ngFor="let key of overlapKGKeys" [(ngModel)]="selectOverlapKGByKey[key]">{{ SvFilterOverlapKG[key] }}</mat-checkbox>
            </div>
          </ng-container>
          <!-- Minimum Interval Depth -->
          <ng-container *ngIf="sec === 'minIntervalDepth'">
            <div class="edit-items">
              <mat-form-field appearance="outline" floatLabel="always">
                <mat-label>min</mat-label>
                <input
                  matInput
                  inputmode="numeric"
                  [(ngModel)]="minIntervalDepthMin"
                  name="minIntervalDepthMin"
                  [placeholder]="INTERVAL_DEPTH_MIN + ' - ' + INTERVAL_DEPTH_MAX"
                  rangeValidator
                  [min]="INTERVAL_DEPTH_MIN"
                  [max]="INTERVAL_DEPTH_MAX"
                  #minIntervalDepthMinModel="ngModel"
                  autocomplete="off"
                >
                <mat-error *ngIf="minIntervalDepthMinModel.errors?.['whitespace']">
                  Spaces are not allowed
                </mat-error>
                <mat-error *ngIf="!minIntervalDepthMinModel.errors?.['whitespace'] && minIntervalDepthMinModel.invalid">
                  Input numbers(range. {{INTERVAL_DEPTH_MIN}} - {{INTERVAL_DEPTH_MAX}})
                </mat-error>
              </mat-form-field>
              <mat-form-field appearance="outline" floatLabel="always">
                <mat-label>max</mat-label>
                <input
                  matInput
                  inputmode="numeric"
                  [(ngModel)]="minIntervalDepthMax"
                  name="minIntervalDepthMax"
                  [placeholder]="INTERVAL_DEPTH_MIN + ' - ' + INTERVAL_DEPTH_MAX"
                  rangeValidator
                  [min]="INTERVAL_DEPTH_MIN"
                  [max]="INTERVAL_DEPTH_MAX"
                  #minIntervalDepthMaxModel="ngModel"
                  autocomplete="off"
                >
                <mat-error *ngIf="minIntervalDepthMaxModel.errors?.['whitespace']">
                  Spaces are not allowed
                </mat-error>
                <mat-error *ngIf="!minIntervalDepthMaxModel.errors?.['whitespace'] && minIntervalDepthMaxModel.invalid">
                  Input numbers(range. {{INTERVAL_DEPTH_MIN}} - {{INTERVAL_DEPTH_MAX}})
                </mat-error>
              </mat-form-field>
            </div>
          </ng-container>
          <!-- Median Interval Depth -->
          <ng-container *ngIf="sec === 'medianIntervalDepth'">
            <div class="edit-items">
              <mat-form-field appearance="outline" floatLabel="always">
                <mat-label>min</mat-label>
                <input
                  matInput
                  inputmode="numeric"
                  [(ngModel)]="medianIntervalDepthMin"
                  name="medianIntervalDepthMin"
                  [placeholder]="INTERVAL_DEPTH_MIN + ' - ' + INTERVAL_DEPTH_MAX"
                  rangeValidator
                  [min]="INTERVAL_DEPTH_MIN"
                  [max]="INTERVAL_DEPTH_MAX"
                  #medianIntervalDepthMinModel="ngModel"
                  autocomplete="off"
                >
                <mat-error *ngIf="medianIntervalDepthMinModel.errors?.['whitespace']">
                  Spaces are not allowed
                </mat-error>
                <mat-error *ngIf="!medianIntervalDepthMinModel.errors?.['whitespace'] && medianIntervalDepthMinModel.invalid">
                  Input numbers(range. {{INTERVAL_DEPTH_MIN}} - {{INTERVAL_DEPTH_MAX}})
                </mat-error>
              </mat-form-field>
              <mat-form-field appearance="outline" floatLabel="always">
                <mat-label>max</mat-label>
                <input
                  matInput
                  inputmode="numeric"
                  [(ngModel)]="medianIntervalDepthMax"
                  name="medianIntervalDepthMax"
                  [placeholder]="INTERVAL_DEPTH_MIN + ' - ' + INTERVAL_DEPTH_MAX"
                  rangeValidator
                  [min]="INTERVAL_DEPTH_MIN"
                  [max]="INTERVAL_DEPTH_MAX"
                  #medianIntervalDepthMaxModel="ngModel"
                  autocomplete="off"
                >
                <mat-error *ngIf="medianIntervalDepthMaxModel.errors?.['whitespace']">
                  Spaces are not allowed
                </mat-error>
                <mat-error *ngIf="!medianIntervalDepthMaxModel.errors?.['whitespace'] && medianIntervalDepthMaxModel.invalid">
                  Input numbers(range. {{INTERVAL_DEPTH_MIN}} - {{INTERVAL_DEPTH_MAX}})
                </mat-error>
              </mat-form-field>
            </div>
          </ng-container>
          <!-- Quality Score -->
          <ng-container *ngIf="sec === 'qualityScore'">
            <div class="edit-items">
              <mat-form-field appearance="outline" floatLabel="always">
                <mat-label>min</mat-label>
                <input
                  matInput
                  inputmode="numeric"
                  [(ngModel)]="qualityMin"
                  name="qualityMin"
                  [placeholder]="QUALITY_SCORE_MIN + ' - ' + QUALITY_SCORE_MAX"
                  rangeValidator
                  [min]="QUALITY_SCORE_MIN"
                  [max]="QUALITY_SCORE_MAX"
                  #qualityMinModel="ngModel"
                  autocomplete="off"
                >
                <mat-error *ngIf="qualityMinModel.errors?.['whitespace']">
                  Spaces are not allowed
                </mat-error>
                <mat-error *ngIf="!qualityMinModel.errors?.['whitespace'] && qualityMinModel.invalid">
                  Input numbers(range. {{QUALITY_SCORE_MIN}} - {{QUALITY_SCORE_MAX}})
                </mat-error>
              </mat-form-field>
              <mat-form-field appearance="outline" floatLabel="always">
                <mat-label>max</mat-label>
                <input
                  matInput
                  inputmode="numeric"
                  [(ngModel)]="qualityMax"
                  name="qualityMax"
                  [placeholder]="QUALITY_SCORE_MIN + ' - ' + QUALITY_SCORE_MAX"
                  rangeValidator
                  [min]="QUALITY_SCORE_MIN"
                  [max]="QUALITY_SCORE_MAX"
                  #qualityMaxModel="ngModel"
                  autocomplete="off"
                >
                <mat-error *ngIf="qualityMaxModel.errors?.['whitespace']">
                  Spaces are not allowed
                </mat-error>
                <mat-error *ngIf="!qualityMaxModel.errors?.['whitespace'] && qualityMaxModel.invalid">
                  Input numbers(range. {{QUALITY_SCORE_MIN}} - {{QUALITY_SCORE_MAX}})
                </mat-error>
              </mat-form-field>
            </div>
          </ng-container>
          <!-- SV Reason -->
          <ng-container *ngIf="sec === 'svReason'">
            <div class="checkbox-group">
              <mat-checkbox *ngFor="let key of svReasonKeys" [(ngModel)]="selectSVReasonByKey[key]">{{ SvFilterSVReason[key] }}</mat-checkbox>
            </div>
          </ng-container>
          <!-- SV Breakpoint Molecule Check -->
          <ng-container *ngIf="sec === 'svBreakpointMoleculeCheck'">
            <div class="checkbox-group">
              <mat-checkbox *ngFor="let key of molCheckKeys" [(ngModel)]="selectMolCheckByKey[key]">{{ SvFilterMolCheck[key] }}</mat-checkbox>
            </div>
          </ng-container>
          <!-- SVs Below Presence in Control DB (%) -->
          <ng-container *ngIf="sec === 'svsBelowPresenceControlDb'">
            <div class="edit-svs-items">
              <div class="form-row">
                <mat-form-field appearance="outline" floatLabel="always">
                  <mat-label>CoLoRSdb</mat-label>
                  <input
                    matInput
                    inputmode="numeric"
                    [(ngModel)]="svsBelowPresenceCoLoRSdBControlDb"
                    name="svsBelowPresenceCoLoRSdBControlDb"
                    [placeholder]="PRESENCE_MIN + ' - ' + PRESENCE_MAX"
                    rangeValidator
                    [min]="PRESENCE_MIN"
                    [max]="PRESENCE_MAX"
                    #svsBelowPresenceCoLoRSdBControlDbModel="ngModel"
                    autocomplete="off"
                  >
                  <mat-error *ngIf="svsBelowPresenceCoLoRSdBControlDbModel.errors?.['whitespace']">
                    Spaces are not allowed
                  </mat-error>
                  <mat-error *ngIf="!svsBelowPresenceCoLoRSdBControlDbModel.errors?.['whitespace'] && svsBelowPresenceCoLoRSdBControlDbModel.invalid">
                    Input numbers(range. {{PRESENCE_MIN}} - {{PRESENCE_MAX}})
                  </mat-error>
                </mat-form-field>
                <span class="cutoff-label">% Cutoff (Only show SVs below % cutoff)</span>
              </div>
              <div class="form-row">
                <mat-form-field appearance="outline" floatLabel="always">
                  <mat-label>gnomAD</mat-label>
                  <input
                    matInput
                    inputmode="numeric"
                    [(ngModel)]="svsBelowPresenceGnomADControlDb"
                    name="svsBelowPresenceGnomADControlDb"
                    [placeholder]="PRESENCE_MIN + ' - ' + PRESENCE_MAX"
                    rangeValidator
                    [min]="PRESENCE_MIN"
                    [max]="PRESENCE_MAX"
                    #svsBelowPresenceGnomADControlDbModel="ngModel"
                    autocomplete="off"
                  >
                  <mat-error *ngIf="svsBelowPresenceGnomADControlDbModel.errors?.['whitespace']">
                    Spaces are not allowed
                  </mat-error>
                  <mat-error *ngIf="!svsBelowPresenceGnomADControlDbModel.errors?.['whitespace'] && svsBelowPresenceGnomADControlDbModel.invalid">
                    Input numbers(range. {{PRESENCE_MIN}} - {{PRESENCE_MAX}})
                  </mat-error>
                </mat-form-field>
                <span class="cutoff-label">% Cutoff (Only show SVs below % cutoff)</span>
              </div>
              <div class="form-row">
                <mat-form-field appearance="outline" floatLabel="always">
                  <mat-label>ONT LRS</mat-label>
                  <input
                    matInput
                    inputmode="numeric"
                    [(ngModel)]="svsBelowPresenceOntLRSControlDb"
                    name="svsBelowPresenceOntLRSControlDb"
                    [placeholder]="PRESENCE_MIN + ' - ' + PRESENCE_MAX"
                    rangeValidator
                    [min]="PRESENCE_MIN"
                    [max]="PRESENCE_MAX"
                    #svsBelowPresenceOntLRSControlDbModel="ngModel"
                    autocomplete="off"
                  >
                  <mat-error *ngIf="svsBelowPresenceOntLRSControlDbModel.errors?.['whitespace']">
                    Spaces are not allowed
                  </mat-error>
                  <mat-error *ngIf="!svsBelowPresenceOntLRSControlDbModel.errors?.['whitespace'] && svsBelowPresenceOntLRSControlDbModel.invalid">
                    Input numbers(range. {{PRESENCE_MIN}} - {{PRESENCE_MAX}})
                  </mat-error>
                </mat-form-field>
                <span class="cutoff-label">% Cutoff (Only show SVs below % cutoff)</span>
              </div>
            </div>
          </ng-container>
          <!-- Masked -->
          <ng-container *ngIf="sec === 'masked'">
            <div class="checkbox-group">
              <mat-checkbox *ngFor="let key of maskedKeys" [(ngModel)]="selectMaskedByKey[key]">{{ SvFilterMasked[key] }}</mat-checkbox>
            </div>
          </ng-container>
          <!-- Best SV per Cluster -->
          <ng-container *ngIf="sec === 'bestSVperCluster'">
            <div class="checkbox-group">
              <mat-checkbox *ngFor="let key of bestSVperClusterKeys" [(ngModel)]="selectBestSVperClusterByKey[key]">{{ SvFilterBestSVperCluster[key] }}</mat-checkbox>
            </div>
          </ng-container>
          <!-- Save / Load -->
          <ng-container *ngIf="sec === 'saves'">
              <div class="saves">
                <!-- Save-as -->
                <section class="preset-save">
                  <mat-form-field appearance="outline" class="w-100">
                    <mat-label>Preset name</mat-label>
                    <input
                      matInput
                      inputmode="numeric"
                      [(ngModel)]="saveName">
                  </mat-form-field>
                  <app-buttons
                    name="Save"
                    buttonType="mat-flat-button"
                    [buttonStyle]="isDisableSave ? 'btn-filled-small-disabled' : 'btn-filled-small'"
                    [isDisabled]="isDisableSave"
                    (buttonClickEvent)="onSaveSvFilter(saveName)"
                  ></app-buttons>
                </section>
                <!-- List -->
                <div class="preset-list">
                  <mat-card *ngFor="let p of saveList">
                    <section  class="card-action">
                      <button class="btn-text-warn btn-delete-filter" mat-stroked-button color="warn" (click)="deleteSvFilter(p)">
                        <app-icons [icon]="'delete'" />
                      </button>
                      <app-buttons
                        name="Load"
                        buttonType="mat-flat-button"
                        buttonStyle="btn-filled-small"
                        [isDisabled]="isInvalid(activeSection)"
                        (buttonClickEvent)="loadFilter(p)"
                      ></app-buttons>
                    </section>
                    <mat-card-title>
                      {{ p.label }}
                    </mat-card-title>
                  </mat-card>
                </div>
              </div>
          </ng-container>
        </div>
      </mat-drawer>
      <!-- summary cards grid -->
      <div class="summary-grid">
        <ng-container *ngFor="let key of cardKeys">
          <mat-card [class.hidden]="cardItems[key].items.length <= 0" class="summary-card">
            <button mat-icon-button class="card-action" (click)="openSection(key)">
              <app-icons class="card-action" [icon]="'edit'" />
            </button>
            <mat-card-header>
              <mat-card-title>{{ cardItems[key].title }}</mat-card-title>
            </mat-card-header>
            <mat-card-content>
              <div class="chips">
                <mat-chip-set>
                  <!-- Regular items (up to the limit, or all items when expanded) -->
                  <mat-chip
                    #chipEl
                    class="summary-chip"
                    [disableRipple]="true"
                    Chip
                    *ngFor="
                      let chip of (
                        cardExpanded[key] 
                          ? cardItems[key].items 
                          : (cardItems[key].items | slice:0:MAX_DISPLAY)
                      ); let i = index
                    "
                    [attr.data-key]="key"
                    [attr.data-index]="i"
                    appearance="outlined"
                    [matTooltip]="(chip.sub ? chip.sub + ' ' : '') + chip.main"
                  >
                    <span *ngIf="chip?.sub" class="chip-sub">{{ chip.sub }}</span>
                    <span class="chip-main">{{ chip.main }}</span>
                  </mat-chip>
                  <!-- Show "+ more" when exceeding the limit and in collapsed state -->
                  <mat-chip
                    #chipEl
                    *ngIf="!cardExpanded[key] && cardItems[key].items.length > MAX_DISPLAY"
                    appearance="outlined"
                    (click)="toggleExpand(key)"
                    class="more-chip"
                  >+{{ cardItems[key].items.length - MAX_DISPLAY }}</mat-chip>
                  <!-- Show "–" when expanded -->
                  <mat-chip
                    #chipEl
                    *ngIf="cardExpanded[key]"
                    appearance="outlined"
                    (click)="toggleExpand(key)"
                    class="less-chip"
                  >Show less</mat-chip>
                </mat-chip-set>
              </div>
            </mat-card-content>
          </mat-card>
        </ng-container>
      </div>
    </mat-drawer-container>
  </mat-dialog-content>
  <!-- Footer -->
  <mat-dialog-actions align="end" class="dialog-actions">
    <section name="LeftButtons">
      <button
        name="DefaultFilter"
        [class]="isEditButtonDisable ? 'btn-text-disabled' : 'btn-text'"
        [disabled]="isEditButtonDisable"
        mat-stroked-button
        (click)="onDefaultFilter()">
        Default Filter
      </button>
      <button
        name="ClearAllFilter"
        [class]="isEditButtonDisable ? 'btn-text-disabled' : 'btn-text'"
        [disabled]="isEditButtonDisable"
        mat-stroked-button
        (click)="onClearAllFilter()">
        Clear All Filter
      </button>
    </section>
    <app-buttons
      name="Cancel"
      buttonType="mat-button"
      [buttonStyle]="isEditButtonDisable ? 'btn-text-disabled' : 'btn-text'"
      [isDisabled]="isEditButtonDisable"
      (buttonClickEvent)="onCancel()"
    ></app-buttons>
    <app-buttons
      name="OK"
      buttonType="mat-flat-button"
      [buttonStyle]="isEditButtonDisable ? 'btn-filled-small-disabled' : 'btn-filled-small'"
      [isDisabled]="isEditButtonDisable"
      (buttonClickEvent)="onOk()"
    ></app-buttons>
  </mat-dialog-actions>
</ng-container>
